import * as React from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';

import { appStore } from './AppStore';
import { Layout } from './Components/Layout';
import { AppContainer } from '@app/Components/AppContainer';
import { AzureAuth, UserPermissions } from '@app/Components/AzureAuth';
import { NotFound } from './Pages/NotFound/NotFound';
import { Loading } from '@app/Components/Loading';
import { EdwRoute, IEdwRouteRouteProps } from './EdwRoute';
import { TradeUnionExportType } from './Models/WebApiModels';

const Home = React.lazy(() => import('./Pages/Home/Home'));
const HotelManagementDaily = React.lazy(() => import('./Pages/HotelManagement/HotelManagementDaily'));
const HotelManagementMonthly = React.lazy(() => import('./Pages/HotelManagement/HotelManagementMonthly'));
const VacManagement = React.lazy(() => import('./Pages/VacManagement/VacManagement'));
const ReserveManagement = React.lazy(() => import('./Pages/ReserveManagement/ReserveManagement'));
const HealthBoard = React.lazy(() => import('./Pages/HealthBoard/HealthBoard'));
const JobList = React.lazy(() => import('./Pages/JobList/JobList'));
const HomeStudy = React.lazy(() => import('./Pages/HomeStudy/HomeStudy'));
const ChangeLog = React.lazy(() => import('./Pages/ChangeLog/ChangeLog'));

const PersonReport = React.lazy(() => import('./Pages/PersonReport/PersonReport'));
const StandbyReport = React.lazy(() => import('./Pages/StandbyReport/StandbyReport'));
const RecurrentTrainingReport = React.lazy(() => import('./Pages/RecurrentTrainingReport/RecurrentTrainingReport'));
const PlannedTrainingsReport = React.lazy(() => import('./Pages/PlannedTrainingsReport/PlannedTrainingsReport'));
const AirportRecencyReport = React.lazy(() => import('./Pages/AirportRecencyReport/AirportRecencyReport'));
const AreaRouteCompetenceReport = React.lazy(() => import('./Pages/AreaRouteCompetenceReport/AreaRouteCompetenceReport'));
const SectorsReport = React.lazy(() => import('./Pages/SectorsReport/SectorsReport'));
const CrewDutyListReport = React.lazy(() => import('./Pages/CrewDutyListReport/CrewDutyListReport'));
const RosterReport = React.lazy(() => import('./Pages/RosterReport/RosterReport'));
const CommentReport = React.lazy(() => import('./Pages/CommentReport/CommentReport'));
const OffDaysReport = React.lazy(() => import('./Pages/OffDaysReport/OffDaysReport'));
const TzvReport = React.lazy(() => import('./Pages/TzvReport/TzvReport'));
const UrgentDutyReport = React.lazy(() => import('./Pages/UrgentDutyReport/UrgentDutyReport'));
const AircraftTypeRecencyCockpitReport = React.lazy(() => import('./Pages/AircraftTypeRecencyReport/AircraftTypeRecencyCockpitReport'));
const AircraftTypeRecencyCabinReport = React.lazy(() => import('./Pages/AircraftTypeRecencyReport/AircraftTypeRecencyCabinReport'));
const DooxReport = React.lazy(() => import('./Pages/DooxReport/DooxReport'));
const DownloadTempFile = React.lazy(() => import('./Pages/DownloadTempFile/DownloadTempFile'));
const ExperienceReport = React.lazy(() => import('./Pages/ExperienceReport/ExperienceReport'));
const SimulatorLicenseStatusReport = React.lazy(() => import('./Pages/SimulatorLicenseStatusReport/SimulatorLicenseStatusReport'));
const XStarMonitoringReport = React.lazy(() => import('./Pages/XStarMonitoringReport/XStarMonitoringReport'));
const CateringReport = React.lazy(() => import('./Pages/CateringReport/CateringReport'));
const GroundStatisticsReport = React.lazy(() => import('./Pages/GroundStatisticsReport/GroundStatisticsReport'));
const DosimetryReport = React.lazy(() => import('./Pages/DosimetryReport/DosimetryReport'));
const ErrorReport = React.lazy(() => import('./Pages/ErrorReport/ErrorReport'));
const SeniorityReport = React.lazy(() => import('./Pages/SeniorityReport/SeniorityReport'));
const SimulatorReport = React.lazy(() => import('./Pages/SimReport/SimReport'));

const Maintenance = React.lazy(() => import('./Pages/Maintenance/Maintenance'));
const JeppesenTool = React.lazy(() => import('./Pages/JeppesenTool/JeppesenTool'));
const BlockDaysManagement = React.lazy(() => import('./Pages/BlockDaysManagement/BlockDaysManagement'));

const RotationTool = React.lazy(() => import('./Pages/RotationTool/RotationTool'));
const RequestTool = React.lazy(() => import('./Pages/RequestTool/RequestTool'));
const RequestCapacityTool = React.lazy(() => import('./Pages/RequestCapacityTool/RequestCapacityTool'));

const PersonVacYearDetails = React.lazy(() => import('./Pages/VacManagement/PersonVacYearDetails'));
const Configs = React.lazy(() => import('./Pages/Configs/Configs'));
const AeroPersInterface = React.lazy(() => import('./Pages/TradeUnionInterface/TradeUnionInterface'));
const LicenseManagement = React.lazy(() => import('./Pages/LicenseManagement/LicenseManagement'));
const EmailViewer = React.lazy(() => import('./Pages/EmailViewer/EmailViewer'));
const TemplatesManagement = React.lazy(() => import('./Pages/TemplatesManagement/TemplatesManagement'));
const CrewApiGenDecConfig = React.lazy(() => import('./Pages/CrewApi/CrewApiGenDecConfig'));
const CrewApiConfigReport = React.lazy(() => import('./Pages/CrewApi/CrewApiConfigReport'));
const GenDecConfigReport = React.lazy(() => import('./Pages/CrewApi/GenDecConfigReport'));
const CustomPersonManagement = React.lazy(() => import('./Pages/CustomPersonManagement/CustomPersonManagement'));
const GenDec = React.lazy(() => import('./Pages/GenDec/GenDec'));
const PermissionsTool = React.lazy(() => import('./Pages/PermissionsTool/PermissionsTool'));
const CourseManagement = React.lazy(() => import('./Pages/CourseManagement/CourseManagement'));
const CcaReport = React.lazy(() => import('./Pages/CabinCrewAttestationReport/CabinCrewAttestationReport'));
const CrewNotificationTimeReport = React.lazy(() => import('./Pages/CrewNotificationTimeReport/CrewNotificationTimeReport'));

export default class Routes {
    static get routs() {
        return <AppContainer>{Routes.getLayout()}</AppContainer>;
    }

    static getLayout() {
        const rosterPermissions = AzureAuth.getRosterPermissions();
        const hasAccessToVacManagement = AzureAuth.hasAccess(UserPermissions.VacManagementRead);
        const hasAccessToReserveManagement = AzureAuth.hasAccess(UserPermissions.ReserveManagementRead);
        const hasAccessToRoster = AzureAuth.hasRosterAccess();
        const hasAccessToCockpit = AzureAuth.hasAccess(UserPermissions.RosterCockpitRead);
        const hasAccessToCabin = AzureAuth.hasAccess(UserPermissions.RosterCabinRead);
        const hasAccessToDutyRelatedReport = AzureAuth.hasAccess(UserPermissions.DutyRelatedReportsRead);
        const hasAccessToPersonReport = hasAccessToDutyRelatedReport && AzureAuth.hasAccess([
            UserPermissions.ReadPersonDetails,
            UserPermissions.ReadPersonDetailsCockpit,
            UserPermissions.ReadPersonDetailsCabin,
            UserPermissions.ReadPersonDetailsGround
        ]);
        const hasAccessToCateringReport = AzureAuth.hasAccess(UserPermissions.CateringReportRead);
        const hasAccessToJCR = AzureAuth.hasAccess(UserPermissions.JCRImportRead);
        const hasAccessToLicenseManagement = AzureAuth.hasAccess(UserPermissions.LicenseManagementRead);
        const hasAccessToGenDec = AzureAuth.hasAccess(UserPermissions.GenDecRead);
        const hasAccessToRotationTool = AzureAuth.hasAccess(UserPermissions.RotationToolRead);
        const hasAccessToAeropers = AzureAuth.hasAccess(UserPermissions.ToolAeroPersRead);
        const hasAccessToEfa = AzureAuth.hasAccess(UserPermissions.ToolEfaRead);
        const hasAccessToHotelTools = AzureAuth.hasAccess(UserPermissions.ToolHotelManagement);
        const hasAccessToCapacityTool = AzureAuth.hasAccess(UserPermissions.RequestCapacityTool);
        const hasAccessToBlockDaysManagement = AzureAuth.hasAccess(UserPermissions.BlockingDaysFlightsRead);
        const hasAccessToCrewApi = AzureAuth.hasAccess(UserPermissions.CrewApiRead);
        const hasAccessToCrewApiGenDecConfig = AzureAuth.hasAccess([UserPermissions.CrewApiRead, UserPermissions.GenDecRead]);
        const hasAccessToCustomPersonManagement = AzureAuth.hasAccess([UserPermissions.MarshalsInfoRead, UserPermissions.FseInfoRead]);
        const hasAccessToTemplatesManagement = AzureAuth.hasAccess(UserPermissions.TemplateManagementRead);
        const hasAccessToAdminTools = AzureAuth.hasAccess(UserPermissions.AdminToolsRead);
        const hasAccessToRequestTool = AzureAuth.hasCrewPortalAccess();
        const hasAccessToHomeStudy = AzureAuth.hasAccess([
            UserPermissions.RosterCabinWritePlanning,
            UserPermissions.RosterCabinWriteTracking,
            UserPermissions.RosterCockpitWritePlanning,
            UserPermissions.RosterCockpitWriteTracking,
            UserPermissions.RosterGroundWritePlanning,
            UserPermissions.RosterGroundWriteTracking
        ]);
        const hasAccessToChangeLog = AzureAuth.hasAccess(UserPermissions.ChangeLogRead);
        const hasAccessToGroundStaff = AzureAuth.hasAccess(rosterPermissions);
        const hasAccessToAreaRouteCompetenceReport = AzureAuth.hasAccess(UserPermissions.RosterCockpitRead);
        const hasAccessToEmailViewer = AzureAuth.hasAccess(UserPermissions.EmailsRead);
        const hasAccessToCaCoReport = hasAccessToCockpit || hasAccessToCabin;
        const hasAccessToDosimetryReport = (hasAccessToCockpit || hasAccessToCabin) && hasAccessToDutyRelatedReport;
        const hasAccessToSeniorityReport = AzureAuth.hasAccess(UserPermissions.SeniorityReportRead);
        const hasAccessToCourseManagement = AzureAuth.hasAccess(UserPermissions.CourseManagementRead);

        const isCrewPortal = appStore.isCrewPortalDns;

        const routes: IEdwRouteRouteProps[] = [];

        const crewPageRoutes: IEdwRouteRouteProps[] = [];
        const crewPortalRoutes: IEdwRouteRouteProps[] = [];

        hasAccessToRoster && crewPageRoutes.push({ exact: true, path: '/', component: Home });

        hasAccessToRequestTool && crewPortalRoutes.push({ path: ['/crew-portal/:type', '/crew-portal'], component: RequestTool });
        hasAccessToRequestTool && crewPortalRoutes.push({ path: ['/request-tool/:type', '/request-tool'], component: RequestTool });

        hasAccessToChangeLog && crewPageRoutes.push({ path: '/change-log', component: ChangeLog });
        hasAccessToVacManagement && crewPageRoutes.push({ path: '/vacation-request-management', component: VacManagement });
        hasAccessToReserveManagement && crewPageRoutes.push({ path: '/reserve-request-management', component: ReserveManagement });
        hasAccessToHomeStudy && crewPageRoutes.push({ path: '/home-study', component: HomeStudy });
        hasAccessToDutyRelatedReport && crewPageRoutes.push({ path: '/urgent-duty-print', component: UrgentDutyReport });

        hasAccessToBlockDaysManagement && crewPageRoutes.push({ path: '/request-tool-configuration', component: BlockDaysManagement });
        hasAccessToTemplatesManagement && crewPageRoutes.push({ path: '/templates-management', component: TemplatesManagement });
        hasAccessToEmailViewer && crewPageRoutes.push({ path: '/email-viewer', component: EmailViewer });

        hasAccessToCrewApiGenDecConfig && crewPageRoutes.push({ path: '/crewapi-gendec-configuration', component: CrewApiGenDecConfig });
        hasAccessToCrewApi && crewPageRoutes.push({ path: '/reports/crew-api-config', component: CrewApiConfigReport });
        hasAccessToGenDec && crewPageRoutes.push({ path: '/reports/gen-dec-config', component: GenDecConfigReport });

        hasAccessToHotelTools && crewPageRoutes.push({ path: '/hotel-management-daily', component: HotelManagementDaily });
        hasAccessToHotelTools && crewPageRoutes.push({ path: '/hotel-management-monthly', component: HotelManagementMonthly });

        hasAccessToJCR && crewPageRoutes.push({ path: '/jeppesen-tool', component: JeppesenTool });
        hasAccessToRotationTool && crewPageRoutes.push({ path: '/rotation-tool', component: RotationTool });
        hasAccessToCapacityTool && crewPageRoutes.push({ path: '/request-capacity-tool', component: RequestCapacityTool });
        hasAccessToHomeStudy && crewPageRoutes.push({ path: '/home-study', component: HomeStudy });
        hasAccessToAeropers && crewPageRoutes.push({ path: '/aeropers-interface', component: AeroPersInterface, additionalProps: { type: TradeUnionExportType.AeroPers, title: 'AeroPers Interface' } });
        hasAccessToEfa && crewPageRoutes.push({ path: '/efa-interface', component: AeroPersInterface, additionalProps: { type: TradeUnionExportType.Efa, title: 'EFA Interface' } });
        hasAccessToLicenseManagement && crewPageRoutes.push({ path: '/license-management', component: LicenseManagement });
        hasAccessToCustomPersonManagement && crewPageRoutes.push({ path: '/custom-person-management', component: CustomPersonManagement });
        hasAccessToGenDec && crewPageRoutes.push({ path: '/gen-dec', component: GenDec });

        hasAccessToPersonReport && crewPageRoutes.push({ path: '/reports/person', component: PersonReport });
        hasAccessToDutyRelatedReport && crewPageRoutes.push({ path: '/reports/recurrent-training', component: RecurrentTrainingReport });
        hasAccessToCaCoReport && crewPageRoutes.push({ path: '/reports/standby', component: StandbyReport });
        hasAccessToDutyRelatedReport && crewPageRoutes.push({ path: '/reports/planned-trainings', component: PlannedTrainingsReport });
        hasAccessToCockpit && crewPageRoutes.push({ path: '/reports/airport-recency', component: AirportRecencyReport });
        hasAccessToAreaRouteCompetenceReport && crewPageRoutes.push({ path: '/reports/area-route-competency-recency', component: AreaRouteCompetenceReport });
        hasAccessToDutyRelatedReport && crewPageRoutes.push({ path: '/reports/sectors', component: SectorsReport });
        hasAccessToDutyRelatedReport && crewPageRoutes.push({ path: '/reports/crew-duty-list', component: CrewDutyListReport });
        (hasAccessToDutyRelatedReport || hasAccessToRoster) && crewPageRoutes.push({ path: '/reports/roster', component: RosterReport });
        hasAccessToDutyRelatedReport && crewPageRoutes.push({ path: '/reports/comment', component: CommentReport });
        hasAccessToDutyRelatedReport && crewPageRoutes.push({ path: '/reports/off-days', component: OffDaysReport });
        hasAccessToCockpit && crewPageRoutes.push({ path: '/reports/aircraft-type-recency-cockpit', component: AircraftTypeRecencyCockpitReport });
        hasAccessToCabin && crewPageRoutes.push({ path: '/reports/aircraft-type-recency-cabin', component: AircraftTypeRecencyCabinReport });
        hasAccessToCaCoReport && crewPageRoutes.push({ path: '/reports/doox', component: DooxReport });
        hasAccessToDutyRelatedReport && crewPageRoutes.push({ path: '/reports/experience', component: ExperienceReport });
        hasAccessToDutyRelatedReport && crewPageRoutes.push({ path: '/reports/simulator-license-status', component: SimulatorLicenseStatusReport });
        hasAccessToDutyRelatedReport && crewPageRoutes.push({ path: '/reports/errors', component: ErrorReport });
        hasAccessToCateringReport && crewPageRoutes.push({ path: '/reports/catering', component: CateringReport });
        hasAccessToDutyRelatedReport && crewPageRoutes.push({ path: '/reports/person-vy-details', component: PersonVacYearDetails });
        hasAccessToDutyRelatedReport && crewPageRoutes.push({ path: '/reports/tzv', component: TzvReport });
        hasAccessToGroundStaff && crewPageRoutes.push({ path: '/reports/ground-staff', component: GroundStatisticsReport });
        hasAccessToDutyRelatedReport && crewPageRoutes.push({ path: '/reports/xstar-monitoring', component: XStarMonitoringReport });
        hasAccessToDosimetryReport && crewPageRoutes.push({ path: '/reports/dosimetry', component: DosimetryReport });
        hasAccessToSeniorityReport && crewPageRoutes.push({ path: '/reports/seniority', component: SeniorityReport });
        hasAccessToCourseManagement && crewPageRoutes.push({ path: '/course-management', component: CourseManagement });
        hasAccessToCourseManagement && crewPageRoutes.push({ path: '/reports/cca-report', component: CcaReport });
        hasAccessToDutyRelatedReport && crewPageRoutes.push({ path: '/reports/simulator', component: SimulatorReport });
        hasAccessToDutyRelatedReport && crewPageRoutes.push({ path: '/reports/crew-notification-time', component: CrewNotificationTimeReport });

        if (!isCrewPortal) {
            routes.push(...crewPageRoutes, ...crewPortalRoutes);

            //system tools are shared between apps for usability, but by direct url
            hasAccessToAdminTools && routes.push({ path: '/health-board', component: HealthBoard });
            hasAccessToAdminTools && routes.push({ path: '/job-list', component: JobList });
            hasAccessToAdminTools && routes.push({ path: '/maintenance', component: Maintenance });
            hasAccessToAdminTools && routes.push({ path: '/configs', component: Configs });
            //no links, but helpful and nothing critical on the page
            routes.push({ path: '/permissions-tool', component: PermissionsTool });
        } else {
            routes.push({ path: '/permissions-tool', component: PermissionsTool });
            hasAccessToRequestTool && routes.push({ path: ['/:type', '/'], component: RequestTool });
        }

        if (crewPageRoutes.length) {
            (window as { hasAccessToCrewPage?: boolean }).hasAccessToCrewPage = true;
        }

        const hasHomeAccess = routes.find(x => x.exact && x.path === '/');
        let firstAvailableRoute = routes.length ? routes[0].path : void 0;
        if (firstAvailableRoute && typeof firstAvailableRoute !== 'string') {
            firstAvailableRoute = firstAvailableRoute[firstAvailableRoute.length - 1];
        }

        //not valid routes to redirect (not-a-page)
        routes.push({ path: ['/download/:id'], component: DownloadTempFile });

        return (
            <Layout>
                <React.Suspense fallback={<Loading isSuspense />}>
                    <Switch>
                        {routes.map((props, index) => <EdwRoute key={index} {...props} />)}

                        {!hasHomeAccess && firstAvailableRoute && <Route exact path="/"><Redirect path="/" to={firstAvailableRoute} /></Route>}

                        <EdwRoute path="*" component={NotFound} />
                    </Switch>
                </React.Suspense>
            </Layout>
        );
    }
}
